/* eslint-disable react/prop-types */
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import { useEffect, useState } from "react";
import { getPaymentOrderHistory } from "services/paymentService";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

const getStatusColor = (status) => {
  const colors = {
    CREATED: "info",
    AUTHORIZED: "warning",
    CAPTURED: "success",
    FAILED: "error",
    CANCELLED: "secondary",
  };

  return colors[status] || "secondary";
};

const getStatusLabel = (status) => {
  const labels = {
    CREATED: "Started",
    AUTHORIZED: "Processing",
    CAPTURED: "Paid",
    FAILED: "Failed",
    CANCELLED: "Cancelled",
  };

  return labels[status] || status || "N/A";
};

const formatFeesType = (feesType) => {
  if (!feesType) return "N/A";
  if (feesType === "CLASS_SESSION_FEES") return "TUTION FEES";
  return feesType.replace(/_/g, " ");
};

const getStudentNames = (students) => {
  if (!Array.isArray(students)) return [];

  return students
    .map((student) => {
      if (typeof student === "string") return student;
      return student?.name || student?.fullName || "";
    })
    .filter(Boolean);
};

function UserCell({ parent, tutor, students }) {
  const studentNames = getStudentNames(students);
  const user = parent || tutor || {};
  const isTutor = Boolean(tutor && !parent);
  const userName = user?.fullName || user?.name || "N/A";
  const userEmail = user?.email || user?.phone || "N/A";
  const parentId = parent?._id || parent?.id;
  const tutorId = tutor?._id || tutor?.id;
  const profilePath = parentId ? `/parents/profile/${parentId}` : tutorId ? `/tutors/profile/${tutorId}` : undefined;

  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={user?.profileImage || null} alt={userName} name={userName} size="sm" variant="rounded" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography
          variant="button"
          fontWeight="medium"
          component={profilePath ? Link : "span"}
          to={profilePath}
          color={profilePath ? "primary" : "text"}
          sx={{
            cursor: profilePath ? "pointer" : "default",
            textDecoration: profilePath ? "underline !important" : "none",
            textUnderlineOffset: profilePath ? "2px" : undefined,
            textDecorationThickness: profilePath ? "1px" : undefined,
            "&:hover": profilePath
              ? {
                  color: "#1976d2",
                  textDecoration: "underline !important",
                }
              : {},
          }}
        >
          {userName}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {isTutor ? "Tutor" : userEmail}
        </ArgonTypography>
        {studentNames.length ? (
          <ArgonTypography variant="caption" color="secondary">
            {studentNames.join(", ")}
          </ArgonTypography>
        ) : null}
      </ArgonBox>
    </ArgonBox>
  );
}

const TextCell = ({ children, color = "text", fontWeight = "medium" }) => (
  <ArgonTypography variant="caption" color={color} fontWeight={fontWeight}>
    {children || "-"}
  </ArgonTypography>
);

const orderMatchesSearch = (order, search) => {
  if (!search) return true;

  const query = search.toLowerCase();
  const parent = order.parentId || {};
  const tutor = order.tutorId || {};
  const studentNames = getStudentNames(order.students).join(" ");
  const searchableText = [
    parent.fullName,
    parent.name,
    parent.email,
    parent.phone,
    tutor.fullName,
    tutor.name,
    tutor.email,
    tutor.phone,
    studentNames,
    order.razorpayOrderId,
    order.razorpayPaymentId,
    order.failureReason,
    order.feesType,
    order.paymentType,
    order.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchableText.includes(query);
};

const PaymentOrderHistoryTableData = (filters = {}) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({
    all: 0,
    started: 0,
    processing: 0,
    paid: 0,
    failed: 0,
    cancelled: 0,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  });

  const fetchPaymentOrders = async () => {
    try {
      setLoading(true);

      const params = {};
      if (filters.fromDate) {
        params.fromdate = dayjs(filters.fromDate).format("YYYY-MM-DD");
      }
      if (filters.toDate) {
        params.todate = dayjs(filters.toDate).format("YYYY-MM-DD");
      }
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.page) {
        params.page = filters.page;
      }
      if (filters.limit) {
        params.limit = filters.limit;
      }

      const countBaseParams = { ...params, page: 1, limit: 1 };
      delete countBaseParams.status;

      const [
        response,
        allCount,
        startedCount,
        processingCount,
        paidCount,
        failedCount,
        cancelledCount,
      ] = await Promise.all([
        getPaymentOrderHistory(params),
        getPaymentOrderHistory(countBaseParams),
        getPaymentOrderHistory({ ...countBaseParams, status: "CREATED" }),
        getPaymentOrderHistory({ ...countBaseParams, status: "AUTHORIZED" }),
        getPaymentOrderHistory({ ...countBaseParams, status: "CAPTURED" }),
        getPaymentOrderHistory({ ...countBaseParams, status: "FAILED" }),
        getPaymentOrderHistory({ ...countBaseParams, status: "CANCELLED" }),
      ]);

      if (response.success) {
        setOrders(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
        }

        setCounts({
          all: allCount?.pagination?.totalItems || 0,
          started: startedCount?.pagination?.totalItems || 0,
          processing: processingCount?.pagination?.totalItems || 0,
          paid: paidCount?.pagination?.totalItems || 0,
          failed: failedCount?.pagination?.totalItems || 0,
          cancelled: cancelledCount?.pagination?.totalItems || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching payment order history:", error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentOrders();
  }, [filters]);

  const columns = [
    { name: "sno", align: "center" },
    { name: "Date", align: "left" },
    { name: "User", align: "left" },
    { name: "Amount", align: "center" },
    { name: "Payment For", align: "left" },
    { name: "Payment Status", align: "center" },
    { name: "Order ID", align: "left" },
    { name: "Payment ID", align: "left" },
    { name: "Reason", align: "left" },
  ];

  const filteredOrders = orders.filter((order) => orderMatchesSearch(order, filters.searchQuery));

  const rows = filteredOrders.map((order, index) => ({
    sno: <TextCell>{index + 1}</TextCell>,
    Date: (
      <TextCell>{order.createdAt ? dayjs(order.createdAt).format("DD MMMM YYYY") : "-"}</TextCell>
    ),
    User: <UserCell parent={order.parentId} tutor={order.tutorId} students={order.students} />,
    Amount: (
      <TextCell color="success">
        {typeof order.amount === "number" ? `₹${order.amount.toLocaleString()}` : "-"}
      </TextCell>
    ),
    "Payment For": <TextCell>{formatFeesType(order.feesType)}</TextCell>,
    "Payment Status": (
      <ArgonBadge
        variant="gradient"
        badgeContent={getStatusLabel(order.status)}
        color={getStatusColor(order.status)}
        size="xs"
        container
      />
    ),
    "Order ID": <TextCell>{order.razorpayOrderId}</TextCell>,
    "Payment ID": <TextCell>{order.razorpayPaymentId}</TextCell>,
    Reason: <TextCell color={order.failureReason ? "error" : "text"}>{order.failureReason}</TextCell>,
  }));

  return {
    columns,
    rows,
    loading,
    pagination,
    fetchPaymentOrders,
    counts,
  };
};

export default PaymentOrderHistoryTableData;
